import React, { FocusEvent, useState, ChangeEvent, useEffect, ReactNode } from 'react'
import classNames from 'classnames'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import uuid from 'uuid'
import Tooltip from '../Tooltip'
import 'scss/5_components/_textarea.scss'

export enum RowSizes {
  large = 6,
  medium = 4,
  small = 1,
  default = 1,
}

export type RowSizeType = keyof typeof RowSizes

export interface ITextAreaProps {
  size?: RowSizeType
  placeholder: string
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void
  label?: string | ReactNode
  id?: string
  required?: boolean
  disabled?: boolean
  value?: string
  name?: string
  dataTest?: string
  invalid?: boolean
  ariaLabel?: string
  toolTipInfo?: string
}

interface IState {
  rows: number
}

const TEXTAREA_LINE_HEIGHT = 24

export const getRowSize = (size: RowSizeType | undefined): number => {
  if (!size || !RowSizes[size]) {
    return RowSizes.default
  }

  return RowSizes[size]
}

export const TextArea = React.memo<ITextAreaProps>(
  ({
    size,
    placeholder,
    onChange,
    label,
    id = uuid(),
    required,
    disabled,
    value = '',
    dataTest,
    onBlur,
    name,
    invalid,
    ariaLabel,
    toolTipInfo,
  }) => {
    const rows = getRowSize(size)
    const initialState = { rows }
    const [state, setState] = useState<IState>(initialState)

    useEffect(() => {
      if (value) {
        const scrollHeight = document.getElementById(id)?.scrollHeight
        const initialRowSize =
          scrollHeight && value ? Math.floor(scrollHeight / TEXTAREA_LINE_HEIGHT) : rows
        setState({ rows: initialRowSize })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      const previousRows = event.target.rows
      event.target.rows = rows // reset number of rows in textarea

      const currentRows = Math.floor(event.target.scrollHeight / TEXTAREA_LINE_HEIGHT)

      if (currentRows === previousRows) {
        event.target.rows = currentRows
      }

      setState({ rows: currentRows })

      if (onChange) onChange(event)
    }

    const classes = classNames('expandable-rounded', { disabled, required, invalid })
    const labelClassname = classNames('label', {
      disabled,
      required,
      invalid,
    })

    return (
      <>
        {label && (
          <label htmlFor={id} id={`label-${id}`} className={labelClassname}>
            {label}
            {toolTipInfo && (
              <Tooltip position='bottom' trigger={<InfoIcon size='small' />}>
                {toolTipInfo}
              </Tooltip>
            )}
          </label>
        )}
        <textarea
          id={id}
          aria-label={ariaLabel}
          rows={state.rows}
          value={value}
          onBlur={onBlur}
          placeholder={placeholder}
          onChange={handleChange}
          className={classes}
          data-test={dataTest}
          disabled={disabled}
          required={required}
          name={name}
        />
      </>
    )
  }
)

export default TextArea
